import fetch from 'isomorphic-fetch'
import { createAction } from 'redux-actions'
import URLSearchParams from 'url-search-params'
import Cookies from 'js-cookie'
import { logException, setCookie } from '../misc/utilities'
import { upload, transcode } from '../api/video'
import { createFetchVideoRequest } from '../api/videos'
import { getUploads, getVideo, getSelectedVideos, isMobile } from '../misc/selectors'
import debounce from 'lodash/debounce'

export const FETCH_VIDEOS = 'FETCH_VIDEOS'
export const FETCH_UPLOADS = 'FETCH_UPLOADS'
export const FETCH_VIDEO = 'FETCH_VIDEO'

export const CLEAR_VIDEOS = 'CLEAR_VIDEOS'
export const CLEAR_VIDEO = 'CLEAR_VIDEO'

export const UPLOAD = 'UPLOAD'
export const UPLOAD_PROGRESS = 'UPLOAD_PROGRESS'
export const RETRANSCODE = 'RETRANSCODE'

export const SHORTCODE_RECEIVED = 'SHORTCODE_RECEIVED'

export const SORT_CHANGED = 'SORT_CHANGED'
export const SEARCH_CHANGED = 'SEARCH_CHANGED'
export const RESET_FILTERS = 'RESET_FILTERS'

export const LOGIN = 'LOGIN'

export const FETCH_CURRENT_USER = 'FETCH_CURRENT_USER'

export const EDIT_USER = 'EDIT_USER'
export const EDIT_VIDEO = 'EDIT_VIDEO'

export const CREATE_VIDEO = 'CREATE_VIDEO'
export const DELETE_VIDEO = 'DELETE_VIDEO'

export const FETCH_STATS = 'FETCH_STATS'
export const FETCH_USER_STATS = 'FETCH_USER_STATS'

export const DESELECT = 'DESELECT'
export const SELECT = 'SELECT'
export const DESELECT_ALL = 'DESELECT_ALL'

export const EXTRACT_URL = 'EXTRACT_URL'
export const CLEAR_CLIPPER = 'CLEAR_CLIPPER'
export const SET_CLIPPER_URL = 'SET_CLIPPER_URL'
export const SET_THUMBNAIL_MODE = 'SET_THUMBNAIL_MODE'

export const ZOOM = 'ZOOM'

export const SET_STATS_DOMAIN = 'SET_STATS_DOMAIN'
export const SET_STATS_INTERVAL = 'SET_STATS_INTERVAL'

export const MESSAGE = 'MESSAGE'

export const SWITCH_VIDEOS_VIEW = 'SWITCH_VIDEOS_VIEW'

export const REMOVE_SUB_ACCOUNT = 'REMOVE_SUB_ACCOUNT'

export const ARTICLE_EXCLUDED = 'ARTICLE_EXCLUDED'

export const UPDATE_EMBED = 'UPDATE_EMBED'

export function fetchCurrentUser() {
  return (dispatch) => {
    dispatch(createAction(FETCH_CURRENT_USER)({ isFetching: true }))
    return fetch(`${process.env.REACT_APP_API_HOST}/me`, {
      credentials: 'include',
    })
    .then(response => response.json())
    .then((json) => {
      const ws = new WebSocket(json.socket)
      ws.onmessage = (e) => {
        const parsedMessage = JSON.parse(e.data)
        if (parsedMessage.type === 'ping') {
          ws.send(JSON.stringify({ type: 'pong' }))
          return
        }
        dispatch(createAction(MESSAGE)(parsedMessage))
      }
      dispatch(createAction(FETCH_CURRENT_USER)({ isFetching: false, ...json }))
    })
    .catch(error => dispatch(createAction(FETCH_CURRENT_USER)(error)))
  }
}
export function fetchVideo(shortcode, version) {
  return (dispatch, getState) => {
    if (getState().video && getState().video.shortcode !== shortcode) {
      dispatch(createAction(CLEAR_VIDEO)())
    }
    dispatch(createAction(FETCH_VIDEO)({ isFetching: true, shortcode }))
    let url = `${process.env.REACT_APP_API_HOST}/videos/${shortcode}`
    if (version) {
      url += `?version=${version}`
    }
    return fetch(url, {
      credentials: 'include',
      headers: {
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
    })
    .then(response => response.json())
    .then((json) => {
      dispatch(createAction(FETCH_VIDEO)({ isFetching: false, ...json }))
      return json
    })
    .catch(error => dispatch(createAction(FETCH_VIDEO)(error)))
  }
}

function editVideoInternal(shortcode, data, dispatch) {
  return fetch(`${process.env.REACT_APP_API_HOST}/videos/${shortcode}`, {
    credentials: 'include',
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Pragma: 'no-cache',
      'Cache-Control': 'no-cache',
    },
    body: JSON.stringify(data),
  })
  .then((response) => {
    if (!response.ok) {
      throw Error(response.statusText)
    }
    return response.json()
  })
  .then(() => {
    dispatch(createAction(EDIT_VIDEO)({ isSaving: false, shortcode }))
  })
  .catch(error => {
    logException(error, {shortcode, data})
    dispatch(createAction(EDIT_VIDEO)(error))
  })
}
const editVideoDebounced = debounce(editVideoInternal, 1000)

export function editVideo(shortcode, data, debounced = false) {
  return (dispatch, getState) => {
    dispatch(createAction(EDIT_VIDEO)({ isSaving: true, shortcode, ...data }))

    if (debounced) {
      editVideoDebounced(shortcode, data, dispatch)
    } else {
      return editVideoInternal(shortcode, data, dispatch)
    }
  }
}

export function errorVideo(shortcode, message) {
  return (dispatch) => {
    dispatch(editVideo(shortcode, {
      status: 3,
      error: message,
    }))
  }
}

export function uploadSpeedTest() {
  return (dispatch, getState) => {
    const { me } = getState()
    const formData = new FormData()
    for (const fieldName of Object.keys(me.speedtest_fields)) {
      formData.append(fieldName, me.speedtest_fields[fieldName])
    }
    let content = ""
    for (let i = 0; i < 50000; i++) {
      content += Math.random().toString(36).substring(10)
    }

    formData.append('file', new Blob([content]))
    let start = Date.now()
    return fetch(me.speedtest_url, {
      method: 'POST',
      body: formData
    })
    .then(response => response.text())
    .then((text) => {
      let delta = Date.now() - start
      dispatch(createAction(EDIT_USER)({uploadSpeed: delta}))
      return delta
    })
  }
}

export function createVideoFromFile(file, options = {}) {
  return (dispatch, getState) => {
    const state = getState()
    const { me } = state
    let fileName
    if (!isMobile(state)) {
      const n = file.name.lastIndexOf(".")
      fileName = n > -1 ? file.name.substr(0, n) : file.name
    }

    const localId = Math.random().toString(36).replace(/[^a-z]+/g, '')
    // Watch out, these might be overriden later by the editVideo
    dispatch(createAction(CREATE_VIDEO)({
      local_id: localId,
      title: options.title || fileName,
    }))
    if (!file.size) {
      dispatch(errorVideo(localId, 'Invalid file size (Try Safari if on iOS)'))
      throw new Error('Invalid file size (Try Safari if on iOS)')
    }
    if (file.size > 1073741824) {
      dispatch(errorVideo(localId, 'File too large. Max 1 gb.'))
      throw new Error('File too large. Max 1 gb.')
    }
    let url = `${process.env.REACT_APP_API_HOST}/shortcode?size=${file.size}`
    if (me.uploadSpeed) {
      url += `&speed=${me.uploadSpeed}`
    }
    return fetch(url, {
      credentials: 'include',
      headers: {
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
    })
    .then(async (response) => {
      if (!response.ok) {
        const message = await response.text()
        throw Error(message + " (" + response.status + ")")
      }
      return response.json()
    })
    .then((metadata) => {
      if (!metadata.shortcode) {
        throw Error("No shortcode present in metadata")
      }
      const shortcode = metadata.shortcode
      const upload_size = file.size
      metadata.local_id = localId
      dispatch(createAction(SHORTCODE_RECEIVED)(metadata))
      dispatch(editVideo(shortcode, {
        original_size: upload_size,
        original_name: file.name,
        upload_source: options.upload_source,
        title: options.title || fileName,
      }))

      let totalTime = 0
      let totalSpeed = 0
      let lastTime = null
      const startTime = Date.now()
      const task = upload(file, { ...metadata.options, ...options }, metadata, (err, data) => {
        if (err) {
          dispatch(errorVideo(shortcode, err.message))
          return
        }
        dispatch(createAction(UPLOAD)({
          shortcode,
          upload_metadata: metadata,
          transcode_options: options,
          transcode_data: data,
        }))
        const upload_time = Date.now() - startTime
        dispatch(editVideo(shortcode, {
          upload_time,
        }))
      }, (data) => {
        let speed = 0
        if (lastTime) {
          const diff = Date.now() - lastTime
          totalTime += diff
          totalSpeed += data.speed * diff
          speed = totalSpeed / totalTime
        }
        lastTime = Date.now()

        dispatch(createAction(UPLOAD_PROGRESS)({
          shortcode,
          upload_percent: data.percent,
          upload_speed: speed,
        }))
      }, () => {
        dispatch(createAction(EDIT_VIDEO)({
          shortcode,
          waitingToUpload: false,
        }))
      })

      dispatch(createAction(EDIT_VIDEO)({
        shortcode,
        waitingToUpload: !!task.queued,
        task,
      }))

      return metadata
    })
    .catch((err) => {
      logException(err, {localId: localId})
      dispatch(errorVideo(localId, err.message))
    })
  }
}

export function createVideoFromFiles(files, options) {
  return (dispatch) => {
    const promises = []
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i]
      promises.push(dispatch(createVideoFromFile(file, {
        upload_source: 'web',
      })))
    }
    return Promise.all(promises)
  }
}

export function createVideo(data) {
  return (dispatch, getState) => fetch(`${process.env.REACT_APP_API_HOST}/videos`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Pragma: 'no-cache',
      'Cache-Control': 'no-cache',
    },
    body: JSON.stringify(data),
    credentials: 'include',
  })
  .then(response => response.json())
  .then((video) => {
    dispatch(createAction(CREATE_VIDEO)(video))
    return video
  })
}

export function createVideoWithOptions(options, data = {}) {
  return (dispatch, getState) => dispatch(createVideo({
    ...data,
    upload_source: options.upload_source,
    title: options.title,
  }))
  .then((video) => {
    const { shortcode, token } = video
    options.shortcode = shortcode
    options.token = token

    return new Promise(
      (resolve, reject) => {
        transcode(options, (err) => {
          if (err) {
            dispatch(errorVideo(shortcode, err.message))
            reject(err)
            return
          }
          dispatch(createAction(UPLOAD)(data))
          resolve(video)
        })
      }
    )
  })
}


export function createVideoFromUrl(url, options = {}, data = {}) {
  return (dispatch, getState) => {
    options.url = url

    return dispatch(createVideoWithOptions(options, data))
  }
}

export function createVideoFromUrls(urls, options = {}, data = {}) {
  return (dispatch, getState) => {
    options.urls = urls

    return dispatch(createVideoWithOptions(options, data))
  }
}

export function pollVideo(shortcode, targetVersion) {
  return (dispatch, getState) => {
    return new Promise(
      (resolve, reject) => {
        const video = getVideo(getState().videos.cache, shortcode)
        if (targetVersion && video.version === targetVersion) {
          resolve()
        } else {
          dispatch(fetchVideo(shortcode, targetVersion))
          .then(() => {
            setTimeout(() => {
              dispatch(pollVideo(shortcode, targetVersion))
              .then(resolve)
            }, 500)
          })
        }
      }
    )
  }
}

export function retranscodeVideo(video, options = {}) {
  return (dispatch) => {
    options.version = (video.max_version || video.version || 0) + 1
    options.url = `https:${  video.files["mp4"] ? video.files["mp4"].url : video.files["mp4-mobile"].url}`
    options.shortcode = video.shortcode
    dispatch(createAction(RETRANSCODE)({
      ...video, new_version: options.version,
    }))
    return new Promise(
      (resolve, reject) => {
        transcode(options, (err, data) => {
          if (err) {
            dispatch(createAction(RETRANSCODE)(err))
            reject(err)
            return
          }
          let output = { ...data, percent: 0, status: 0, new_version: options.version }
          dispatch(createAction(RETRANSCODE)(output))
          resolve(output)
        })
      }
    )
  }
}

export function createThumbnail(video, thumbOffset) {
  return (dispatch) => fetch(`${process.env.REACT_APP_API_HOST}/screenshot/${video.shortcode}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Pragma: 'no-cache',
      'Cache-Control': 'no-cache',
    },
    body: JSON.stringify({ thumb_offset: thumbOffset }),
    credentials: 'include',
  })
  .then(response => response.json())
}

// Only let the current stats fetch come through, otherwise null it out
function checkStatsResult(state, options, json) {
  const { interval, domain, shortcode } = state
  /*if (options.interval !== interval
      || options.domain !== domain
      || options.shortcode !== shortcode) {
    return {}
  }*/
  return json
}

export function fetchUserStats() {
  return (dispatch, getState) => {
    fetch(`${process.env.REACT_APP_API_HOST}/stats/live`, { credentials: 'include' })
          .then(response => response.json())
          .then(json => dispatch(createAction(FETCH_USER_STATS)({ live: json })))
          .catch(error => dispatch(createAction(FETCH_USER_STATS)({ live: null })))
  }
}

export function fetchRealtimeStats(options = {}) {
  return (dispatch, getState) => {
    const { domain, shortcode } = options
    if (shortcode) {
      fetch(`${process.env.REACT_APP_API_HOST}/${shortcode}/stats/live`, { credentials: 'include' })
        .then(response => response.json())
        .then(json => {
          dispatch(createAction(FETCH_STATS)(checkStatsResult(getState().stats, options, { live: json })))
        })
        .catch(error => {
          dispatch(createAction(FETCH_STATS)(error))
        })
    } else if (domain) {
      fetch(`${process.env.REACT_APP_API_HOST}/domain/${domain}/stats/hot`, { credentials: 'include' })
        .then(response => response.json())
        .then(json => dispatch(createAction(FETCH_STATS)(checkStatsResult(getState().stats, options, { hot: json }))))
        .catch((error) => dispatch(createAction(FETCH_STATS)({ hot: null })))
      fetch(`${process.env.REACT_APP_API_HOST}/domain/${domain}/stats/live`, { credentials: 'include' })
        .then(response => response.json())
        .then(json => dispatch(createAction(FETCH_STATS)(checkStatsResult(getState().stats, options, { live: json }))))
        .catch(error => dispatch(createAction(FETCH_STATS)({ live: null })))
    } else {
      fetch(`${process.env.REACT_APP_API_HOST}/stats/hot`, { credentials: 'include' })
        .then(response => response.json())
        .then(json => dispatch(createAction(FETCH_STATS)(checkStatsResult(getState().stats, options, { hot: json }))))
        .catch(error => dispatch(createAction(FETCH_STATS)({ hot: null })))
      fetch(`${process.env.REACT_APP_API_HOST}/stats/live`, { credentials: 'include' })
        .then(response => response.json())
        .then(json => dispatch(createAction(FETCH_STATS)(checkStatsResult(getState().stats, options, { live: json }))))
        .catch(error => dispatch(createAction(FETCH_STATS)({ live: null })))
    }
  }
}

export function fetchStats({ interval, domain, shortcode, trending } = {}) {
  return (dispatch, getState) => {
    const options = {
      interval,
      domain,
      shortcode,
      trending,
    }
    dispatch(createAction(FETCH_STATS)({
      ...options,
    }))

    if (shortcode) {
      fetch(`${process.env.REACT_APP_API_HOST}/${shortcode}/plays`, { credentials: 'include' })
        .then(response => response.json())
        .then(json => {
          dispatch(createAction(FETCH_STATS)(checkStatsResult(getState().stats, options, { plays: json })))
        })
        .catch(error => {
          dispatch(createAction(FETCH_STATS)(error))
        })
      fetch(`${process.env.REACT_APP_API_HOST}/${shortcode}/articles`, { credentials: 'include' })
        .then(response => response.json())
        .then(json => dispatch(createAction(FETCH_STATS)(checkStatsResult(getState().stats, options, { articles: json }))))
        .catch(error => dispatch(createAction(FETCH_STATS)(error)))
    } else if (domain) {
      fetch(`${process.env.REACT_APP_API_HOST}/domain/${domain}/stats?interval=${interval}`, { credentials: 'include' })
        .then(response => response.json())
        .then(json => dispatch(createAction(FETCH_STATS)(checkStatsResult(getState().stats, options, json))))
        .catch(error => dispatch(createAction(FETCH_STATS)(error)))
    } else if (trending) {
      fetch(`${process.env.REACT_APP_API_HOST}/trending/${trending}/articles`, { credentials: 'include' })
        .then(response => response.json())
        .then(json => dispatch(createAction(FETCH_STATS)(checkStatsResult(getState().stats, options, { articles: json }))))
        .catch(error => dispatch(createAction(FETCH_STATS)(error)))
    } else {
      const urlRoot = `${process.env.REACT_APP_API_HOST}/stats/`
      fetch(`${urlRoot}plays`, { credentials: 'include' })
        .then(response => response.json())
        .then(json => dispatch(createAction(FETCH_STATS)(checkStatsResult(getState().stats, options, { plays: json }))))
        .catch(error => dispatch(createAction(FETCH_STATS)({ plays: null })))
    }

    dispatch(fetchRealtimeStats({ interval, domain, shortcode }))
  }
}

export function excludeArticle(domain, article, shortcode) {
  return (dispatch, getState) => {
    return fetch(`${process.env.REACT_APP_API_HOST}/domain/${domain}/exclude`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        article,
        shortcode,
      }),
    })
    .then(response => response.json())
    .then(json => dispatch(createAction(ARTICLE_EXCLUDED)(json)))
  }
}

export function fetchVideos(options, force) {
  return (dispatch, getState) => {
    const { videos } = getState()

    // shallow compare options and video state to see if we're getting new
    let shouldClear = !!force
    for (const key of Object.keys(options)) {
      if (videos[key] !== options[key]) {
        shouldClear = true
      }
    }

    if (shouldClear) {
      dispatch(createAction(CLEAR_VIDEOS)())
    }

    if (shouldClear || !videos.loaded) {
      dispatch(createAction(FETCH_VIDEOS)({ isFetching: true, ...options }))
      return createFetchVideoRequest(options)
      .then(json => dispatch(createAction(FETCH_VIDEOS)({ isFetching: false, ...json })))
      .catch(error => dispatch(createAction(FETCH_VIDEOS)(error)))
    }
  }
}

export function login(email, password) {
  return (dispatch) => {
    dispatch(createAction(LOGIN)({ loggingIn: true }))
    return fetch(`${process.env.REACT_APP_API_HOST}/check`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        username: email,
        password,
      }),
    })
    .then(response => response.json())
    .then((json) => {
      const exists = json.error !== 'UserDoesNotExist' 
      dispatch(createAction(LOGIN)({ loggingIn: false, userExists: exists, ...json }))
      if (!json.error) {
        dispatch(createAction(FETCH_CURRENT_USER)(json))
      }
      return json
    })
    .catch(error => dispatch(createAction(LOGIN)(error)))
  }
}

export function check(email, password) {
  return (dispatch) => {
    return fetch(`${process.env.REACT_APP_API_HOST}/check`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        username: email,
        password,
      }),
    })
    .then(response => response.json())
    .then((json) => {
      const exists = json.error !== 'UserDoesNotExist' 
      dispatch(createAction(LOGIN)({ userExists: exists }))
      return exists
    })
  }
}

export function fbConnect(authToken, email) {
  return (dispatch) => {
    let error = false
    dispatch(createAction(LOGIN)({ loggingIn: true }))
    return fetch(`${process.env.REACT_APP_API_HOST}/fbconnect`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        authToken,
        email,
      }),
    })
    .then(response => response.json())
    .then((json) => {
      dispatch(createAction(LOGIN)({ loggingIn: false, userExists: true, ...json }))
      if (!json.error) {
        dispatch(createAction(FETCH_CURRENT_USER)(json))
      }
      return json
    })
    .catch(err => dispatch(createAction(LOGIN)(err)))
  }
}

export function signup(username, password, email) {
  return (dispatch) => {
    let error = false
    dispatch(createAction(LOGIN)({ loggingIn: true }))
    return fetch(`${process.env.REACT_APP_API_HOST}/users`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        username,
        password,
        email,
      }),
    })
    .then((response) => {
      dispatch(createAction(LOGIN)({ loggingIn: false }))
      if (response.ok) {
        return response.json()
      }
      error = true
      return response.text()
    })
    .then((obj) => {
      if (error) {
        throw new Error(obj)
      }
      return dispatch(login(username, password))
    })
    .catch(err => dispatch(createAction(LOGIN)(err)))
  }
}

export function pollUploads() {
  return (dispatch, getState) => {
    const uploads = getUploads(getState())
    if (uploads.filter((v => !v.status || v.status < 2)).length) {
      fetch(`${process.env.REACT_APP_API_HOST}/poll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Pragma: 'no-cache',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify(uploads.map(v => v.shortcode)),
        credentials: 'include',
      })
      .then(response => response.json())
      .then((json) => {
        dispatch(createAction(FETCH_UPLOADS)(json))
      })
      .catch((error) => {
        console.log("ERROR fetching uploads")
      })
    }
  }
}

export function editUser(data) {
  return (dispatch, getState) => {
    dispatch(createAction(EDIT_USER)(data))
    return fetch(`${process.env.REACT_APP_API_HOST}/me`, {
      credentials: 'include',
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        session: Cookies.get('session'),
        ...data,
      }),
    })
    .then((response) => {
      if (!response.ok) {
        throw Error(response.statusText)
      }
      return response.json()
    })
    .then((json) => {
      dispatch(createAction(EDIT_USER)(json))
    })
    .catch((error) => {
      alert(error.message)
    })
  }
}

export function fetchAds() {
  return (dispatch, getState) => {
    return fetch(`${process.env.REACT_APP_API_HOST}/ads`, { credentials: 'include' })
      .then(response => response.json())
      .then(json => dispatch(createAction(EDIT_USER)(json)))
      .catch(error => dispatch(createAction(EDIT_USER)(error)))
  }
}

export function uploadPic(photo) {
  return (dispatch, getState) => {
    const formData = new FormData()
    formData.append('watermark', photo)

    return fetch(`${process.env.REACT_APP_API_HOST}/me/pic`, {
      credentials: 'include',
      method: 'POST',
      body: formData,
    })
    .then(response => response.json())
    .then(json => dispatch(createAction(EDIT_USER)(json)))
  }
}


export function changePassword(data) {
  return (dispatch, getState) => fetch(`${process.env.REACT_APP_API_HOST}/me/change_password`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Pragma: 'no-cache',
      'Cache-Control': 'no-cache',
    },
    body: JSON.stringify({
      session: Cookies.get('session'),
      ...data,
    }),
  })
  .then(response => response.json())
  .then((json) => {
    if (json.error) {
      alert(json.message)
      return
    }
    dispatch(createAction(EDIT_USER)(data))
  })
  .catch(error => alert('Could not change password'))
}

export function deleteVideo(video) {
  return (dispatch) => {
    if (video.shortcode) {
      dispatch(createAction(DELETE_VIDEO)({ isDeleting: true, ...video }))
      return fetch(`${process.env.REACT_APP_API_HOST}/videos/${video.shortcode}`, {
        credentials: 'include',
        method: 'DELETE',
      })
        .then(response => response.text)
        .then(() => {
          dispatch(createAction(DELETE_VIDEO)({ ...video, isDeleting: false }))
        })
        .catch(error => dispatch(createAction(DELETE_VIDEO)(error)))
    }
    dispatch(createAction(DELETE_VIDEO)({ isDeleting: false, ...video }))
  }
}

export function deleteSelectedVideos() {
  return (dispatch, getState) => {
    const promises = []
    for (const video of getSelectedVideos(getState())) {
      promises.push(dispatch(deleteVideo(video)))
    }
    return Promise.all(promises)
  }
}

export function extractFromUrl(sourceUrl, groupId) {
  return (dispatch, getState) => {
    dispatch(createAction(SET_CLIPPER_URL)({
      group_id: groupId,
      source_url: sourceUrl,
    }))
    dispatch(createAction(EXTRACT_URL)({ isFetching: true }))
    const searchParams = new URLSearchParams()
    searchParams.append('url', sourceUrl)
    return fetch(`${process.env.REACT_APP_API_HOST}/extract?${searchParams}`, { credentials: 'include' })
      .then(response => response.json())
      .then((json) => {
        dispatch(createAction(EXTRACT_URL)({ isFetching: false, ...json }))
      })
      .catch(error => dispatch(createAction(EXTRACT_URL)(error)))
  }
}

export function loadFile(file, groupId) {
  return (dispatch, getState) => {
    dispatch(createAction(SET_CLIPPER_URL)({
      group_id: groupId,
    }))
    dispatch(createAction(EXTRACT_URL)({ 
      url: window.URL.createObjectURL(file),
      mime: file.type,
      file,
    }))
  }
}

export function cancel(video) {
  return (dispatch, getState) => {
    dispatch(deleteVideo(video))
    if (video.task) {
      video.task.cancel()
    }
  }
}

export function clearStats() {
  return (dispatch, getState) => {
    dispatch(createAction(FETCH_STATS)({
      plays: null,
      hot: null,
      pages: null,
      videos: null,
      live: null,
      vtrs: null,
    }))
  }
}

export function forgotPassword(email) {
  const formData = new FormData()
  formData.append('email', email)
  return (dispatch, getState) => fetch(`${process.env.REACT_APP_API_HOST}/users/reset`, {
    credentials: 'include',
    method: 'POST',
    body: formData,
  })
  .then(response => response.ok)
}

export function sendInvite(email) {
  return (dispatch, getState) => fetch(`${process.env.REACT_APP_API_HOST}/invite`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Pragma: 'no-cache',
      'Cache-Control': 'no-cache',
    },
    body: JSON.stringify({ email })
  })
  .then((response) => {
    if (!response.ok) {
      throw Error(response.statusText)
    }
    return response
  })
}

export function verifyInvite(invite) {
  return (dispatch, getState) => fetch(`${process.env.REACT_APP_API_HOST}/verify_invitation?invite=${invite}`, {
    credentials: 'include',
    headers: {
      Pragma: 'no-cache',
      'Cache-Control': 'no-cache',
    },
  })
  .then((response) => {
    if (!response.ok) {
      throw Error(response.statusText)
    }
    return response.json()
  })
  .then((output) => {
    dispatch(createAction(EDIT_USER)({ invite_parent: output.parent }))  
  })
  .catch((error) => {
    dispatch(createAction(EDIT_USER)({ invite_error: true }))  
  })
}

export function removeSubAccount(account) {
  return (dispatch, getState) => {
    dispatch(createAction(REMOVE_SUB_ACCOUNT)(account))
    return fetch(`${process.env.REACT_APP_API_HOST}/sub/${account.user_name}`, {
      credentials: 'include',
      method: 'DELETE',
    })
    .then((response) => {
      if (!response.ok) {
        throw Error(response.statusText)
      }
      return response
    })
  }
}

export function generateEmbedCode(options) {
  return (dispatch, getState) => {
    dispatch(createAction(UPDATE_EMBED)(options))
    return fetch(`${process.env.REACT_APP_API_HOST}/videos/${options.video.shortcode}/embeds/${options.id}`, {
      method: 'POST', 
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({shortcode: options.video.shortcode, ...options}),
      credentials: 'include',
    })
    .then(() => options)
  }
}

export function changePlan(plan) {
  return (dispatch, getState) => {
    dispatch(createAction(EDIT_USER)({loading_plan: true}))
    return fetch(`${process.env.REACT_APP_API_HOST}/me/plan`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({plan}),
      credentials: 'include',
    }).then(response => {
      response.json().then(data => {
        dispatch(createAction(EDIT_USER)({ ...data, loading_plan: false}))
      });
    })
  }
}

export function createStripeCustomer(token, plan) {
  return (dispatch, getState) => {
    token.plan = plan
    dispatch(createAction(EDIT_USER)({loading_plan: true}))
    return fetch(`${process.env.REACT_APP_API_HOST}/me/stripe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify(token),
      credentials: 'include',
    }).then(response => {
      response.json().then(data => {
        dispatch(createAction(EDIT_USER)({ ...data, loading_plan: false}))
      });
    })
  }
}



// WEBPACK FOOTER //
// ./src/actions/index.js